import React, { useEffect, useMemo, useRef, useState } from 'react';
import styled, { useTheme } from 'styled-components';
import { useShallow } from 'zustand/react/shallow';

import { GOOGLE_MAPS_MAP_ID } from '~/constants';
import { LibraryStatus, useGoogleMapsStore } from '~/stores/GoogleMaps';
import { useNotificationStore } from '~/stores/Notification';
import { ApiAddress } from '~/typings/API';

interface MapProps {
  address: ApiAddress;
  height?: string;
  width?: string;
  margin?: string;
}

const MapContainer = styled.div<{ height: string; width: string; margin: string }>`
  position: relative;
  width: ${({ width }) => width};
  height: ${({ height }) => height};
  border-radius: 8px;
  overflow: hidden;
  margin: ${({ margin }) => margin};

  &::before {
    content: '';
    position: absolute;
    inset: 0;
    border-radius: 8px;
    box-shadow: inset 0 0 0 1px rgba(0, 0, 0, 0.075);
    pointer-events: none;
    z-index: 1;
  }

  .gm-style-cc,
  .gm-style a[href^='https://maps.google.com/maps'],
  .gm-style a[href^='https://www.google.com/maps'],
  .gm-style-cc div,
  .gm-style-mtc {
    display: none !important;
  }
`;

const MapInner = styled.div`
  width: 100%;
  height: 100%;
`;

const LoadingPlaceholder = styled.div<{ background: string }>`
  width: 100%;
  height: 100%;
  background: ${({ background }) => background};
  display: flex;
  align-items: center;
  justify-content: center;
`;

const formatAddressForGeocoding = (address: ApiAddress): string => {
  return [
    address.line1,
    address.line2,
    address.city,
    address.state,
    address.postalCode,
    address.countryCode === 'US' ? 'USA' : address.countryCode,
  ]
    .filter(Boolean)
    .join(' ');
};

const darkenColor = (hex: string, percent: number): string => {
  const num = parseInt(hex.replace('#', ''), 16);
  const r = Math.floor(num / 65536);
  const g = Math.floor((num % 65536) / 256);
  const b = num % 256;
  const darkenedR = Math.max(0, Math.floor(r * (1 - percent)));
  const darkenedG = Math.max(0, Math.floor(g * (1 - percent)));
  const darkenedB = Math.max(0, Math.floor(b * (1 - percent)));
  const result = darkenedR * 65536 + darkenedG * 256 + darkenedB;
  return `#${result.toString(16).padStart(6, '0')}`;
};

const buildMarkerContent = (primaryColor: string): HTMLElement => {
  const darkerColor = darkenColor(primaryColor, 0.5);
  const wrapper = document.createElement('div');
  wrapper.innerHTML = `
    <svg width="32" height="40" viewBox="0 0 32 40" fill="none" xmlns="http://www.w3.org/2000/svg">
      <path d="M16 0C7.163 0 0 7.163 0 16C0 24.837 16 40 16 40C16 40 32 24.837 32 16C32 7.163 24.837 0 16 0Z" fill="${primaryColor}"/>
      <circle cx="16" cy="16" r="6" fill="${darkerColor}"/>
    </svg>
  `;
  return wrapper;
};

export const Map = ({ address, height = '132px', width = '400px', margin = '0' }: MapProps) => {
  const theme = useTheme();
  const addDangerNotifcation = useNotificationStore((s) => s.addDangerNotification);
  const { loadLibrary, isMapsLoaded } = useGoogleMapsStore(
    useShallow((s) => ({
      loadLibrary: s.loadLibrary,
      isMapsLoaded:
        s.libraryStatus.maps === LibraryStatus.Loaded &&
        s.libraryStatus.marker === LibraryStatus.Loaded &&
        s.libraryStatus.geocoding === LibraryStatus.Loaded,
    }))
  );
  const containerRef = useRef<HTMLDivElement | null>(null);
  const mapRef = useRef<google.maps.Map | null>(null);
  const markerRef = useRef<google.maps.marker.AdvancedMarkerElement | null>(null);

  const [center, setCenter] = useState<google.maps.LatLngLiteral | null>(null);
  const [isGeocoding, setIsGeocoding] = useState(true);

  useEffect(() => {
    loadLibrary(['maps', 'marker', 'geocoding']).catch(() => {
      addDangerNotifcation({ content: 'Failed to load maps' });
    });
  }, [loadLibrary, addDangerNotifcation]);

  useEffect(() => {
    if (!isMapsLoaded || !address.line1 || !address.city || !address.state || !address.postalCode) {
      return;
    }
    const geocoder = new google.maps.Geocoder();
    geocoder.geocode({ address: formatAddressForGeocoding(address) }, (results, status) => {
      if (status === 'OK' && results && results[0]) {
        const location = results[0].geometry.location;
        setCenter({ lat: location.lat(), lng: location.lng() });
      }
      setIsGeocoding(false);
    });
  }, [isMapsLoaded, address]);

  const isDark = theme.id === 'dark';
  const placeholderBackground = isDark ? '#15171b' : '#efebe2';

  const markerContent = useMemo(() => buildMarkerContent(theme.primary.background), [theme.primary.background]);

  useEffect(() => {
    if (!isMapsLoaded || !center || !containerRef.current) return;

    if (!mapRef.current) {
      mapRef.current = new google.maps.Map(containerRef.current, {
        center,
        zoom: 14,
        mapId: GOOGLE_MAPS_MAP_ID,
        colorScheme: isDark ? google.maps.ColorScheme.DARK : google.maps.ColorScheme.LIGHT,
        disableDefaultUI: true,
        zoomControl: false,
        streetViewControl: false,
        mapTypeControl: false,
        fullscreenControl: false,
        draggableCursor: 'grab',
        draggingCursor: 'grabbing',
      });
    } else {
      mapRef.current.setCenter(center);
    }

    if (markerRef.current) {
      markerRef.current.map = null;
    }
    markerRef.current = new google.maps.marker.AdvancedMarkerElement({
      map: mapRef.current,
      position: center,
      content: markerContent,
    });

    return () => {
      if (markerRef.current) {
        markerRef.current.map = null;
        markerRef.current = null;
      }
      mapRef.current = null;
    };
  }, [isMapsLoaded, center, markerContent, isDark]);

  if (!isMapsLoaded || isGeocoding || !center) {
    return (
      <MapContainer height={height} width={width} margin={margin}>
        <LoadingPlaceholder background={placeholderBackground} />
      </MapContainer>
    );
  }

  return (
    <MapContainer height={height} width={width} margin={margin}>
      <MapInner ref={containerRef} />
    </MapContainer>
  );
};
